import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, qs } from '../api';
import { useMeta } from '../meta';
import { ErrorBox, Panel, StatusBadge, fmtDate, fmtMinutes } from '../components';
import { t } from '../i18n';

interface Summary {
  effort: ProjectEffort[];
  versionEffort: VersionEffort[];
  byStatus: { status: number; n: number }[];
  byProject: { name: string; open: number; resolved: number; closed: number; total: number }[];
  byHandler: { name: string; open: number; resolved: number; closed: number; total: number }[];
  bySeverity: { severity: number; open: number; total: number }[];
}

interface ProjectEffort {
  id: number; name: string; issues: number; resolved: number; story_points: number; estimated_minutes: number; needed_minutes: number | null;
  unestimated: number; coffee: number; unknown: number; needs_refinement: number;
}
/** effort of one active target version of a project */
interface VersionEffort extends ProjectEffort { version_id: number; version: string; date_order: number | null }

const sum = <K extends string>(rows: Record<K, number>[], key: K) => rows.reduce((s, r) => s + Number(r[key] ?? 0), 0);

export function SummaryPage() {
  const { projectId, project, label } = useMeta();
  const { data, error } = useQuery({
    queryKey: ['summary', projectId],
    queryFn: () => api.get<Summary>('/api/summary' + qs({ project: projectId || undefined })),
  });
  return (
    <div>
      <div className="page-head">
        <h1>
          {t('Summary|page')} <span className="muted">— {projectId ? project(projectId)?.name : t('All Projects')}</span>
        </h1>
      </div>
      <ErrorBox error={error} />
      {data && (
        <div className="summary-grid">
          <EffortOverview rows={data.versionEffort ?? []} />
          <Panel title={t('By Status')}>
            <table className="simple">
              <tbody>
                {data.byStatus.map((r) => (
                  <tr key={r.status}>
                    <td>
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="num">
                      <Link to={'/issues' + qs({ status: r.status, project: projectId || undefined, hide_status: 0 })}>{r.n}</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>{t('Total')}</td>
                  <td className="num">
                    <Link to={'/issues' + qs({ project: projectId || undefined, hide_status: 0 })}>{sum(data.byStatus, 'n')}</Link>
                  </td>
                </tr>
              </tfoot>
            </table>
            <StatusBar rows={data.byStatus} projectId={projectId} />
          </Panel>
          <Panel title={t('By Severity')}>
            <table className="simple">
              <thead>
                <tr>
                  <th></th>
                  <th className="num">{t('Open')}</th>
                  <th className="num">{t('Total')}</th>
                </tr>
              </thead>
              <tbody>
                {data.bySeverity.map((r) => (
                  <tr key={r.severity}>
                    <td>{label('severity', r.severity)}</td>
                    <td className="num">{r.open}</td>
                    <td className="num">{r.total}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>{t('Total')}</td>
                  <td className="num">{sum(data.bySeverity, 'open')}</td>
                  <td className="num">{sum(data.bySeverity, 'total')}</td>
                </tr>
              </tfoot>
            </table>
          </Panel>
          <CountTable title={t('By Project')} rows={data.byProject} showProgress />
          <CountTable title={t('By Assignee')} rows={data.byHandler} />
        </div>
      )}
    </div>
  );
}

/** One bar split into the status colors, each part as wide as its share of all issues. */
function StatusBar({ rows, projectId }: { rows: { status: number; n: number }[]; projectId: number | null }) {
  const { label, statusColor } = useMeta();
  const total = sum(rows, 'n');
  if (!total) return null;
  return (
    <div className="status-bar" role="img" aria-label={rows.map((r) => `${label('status', r.status)}: ${r.n}`).join(', ')}>
      {rows
        .filter((r) => r.n > 0)
        .map((r) => (
          <Link
            key={r.status}
            to={'/issues' + qs({ status: r.status, project: projectId || undefined, hide_status: 0 })}
            style={{ flexGrow: r.n, background: statusColor(r.status) }}
            title={`${label('status', r.status)}: ${r.n} (${((100 * r.n) / total).toFixed(1)}%)`}
          />
        ))}
    </div>
  );
}

/** Effort per planned (active, not released) target version; whole projects are less useful for planning. */
function EffortOverview({ rows }: { rows: VersionEffort[] }) {
  const totals = rows.reduce((sum, row) => ({
    issues: sum.issues + row.issues, resolved: sum.resolved + row.resolved, story_points: sum.story_points + Number(row.story_points),
    estimated_minutes: sum.estimated_minutes + Number(row.estimated_minutes),
    needed_minutes: sum.needed_minutes + Number(row.needed_minutes ?? 0), unestimated: sum.unestimated + row.unestimated,
    coffee: sum.coffee + row.coffee, unknown: sum.unknown + row.unknown, needs_refinement: sum.needs_refinement + row.needs_refinement,
  }), { issues: 0, resolved: 0, story_points: 0, estimated_minutes: 0, needed_minutes: 0, unestimated: 0, coffee: 0, unknown: 0, needs_refinement: 0 });
  const canTime = rows.some((row) => row.needed_minutes !== null);
  const progress = (row: { issues: number; resolved: number }) => {
    const pct = row.issues ? Math.round((100 * row.resolved) / row.issues) : 0;
    return (
      <td className="effort-progress" title={t('{done} of {total} resolved ({pct}%)', { done: row.resolved, total: row.issues, pct })}>
        <div className="effort-progress-bar"><span style={{ width: `${pct}%` }} /></div>
        <span className="subtle small">{row.resolved} / {row.issues}</span>
      </td>
    );
  };
  const cells = (row: Omit<ProjectEffort, 'id' | 'name'>) => <>
    {progress(row)}
    <td className="num">{Number(row.story_points)}<div className="subtle small">☕ {row.coffee} · ❓ {row.unknown}</div></td>
    <td className="num">{row.unestimated}</td>
    <td className="num nowrap">{fmtMinutes(Number(row.estimated_minutes))}</td>
    <td className="num nowrap">{row.needed_minutes == null ? '—' : fmtMinutes(Number(row.needed_minutes))}</td>
  </>;
  return (
    <div className="effort-overview">
      <Panel title={t('Planned target versions')}>
        <div className="effort-totals">
          <div><span className="subtle small">{t('Story points')}</span><strong>{totals.story_points}</strong></div>
          <div><span className="subtle small">{t('Estimated time')}</span><strong>{fmtMinutes(totals.estimated_minutes)}</strong></div>
          <div><span className="subtle small">{t('Needed time (actual)')}</span><strong>{canTime ? fmtMinutes(totals.needed_minutes) : '—'}</strong></div>
        </div>
        <p className="subtle small">{t('Only issues planned for a target version that is not released yet are counted.')} {t('Totals include only issues and time entries you can access. Coffee and question-mark estimates are counted separately from numeric points.')}</p>
        {totals.needs_refinement > 0 && <p className="subtle small">{t('{count} issues need refinement, research or a planning pause.', { count: totals.needs_refinement })}</p>}
        {rows.length === 0 ? <p className="subtle">{t('There are no active target versions.')}</p> : (
          <div className="table-wrap">
            <table className="simple">
              <thead><tr><th>{t('Project')} / {t('Target Version')}</th><th>{t('Progress')}</th><th className="num">{t('Story points')}</th><th className="num">{t('Not rated')}</th><th className="num">{t('Estimated time')}</th><th className="num">{t('Needed time (actual)')}</th></tr></thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.version_id}>
                    <td>
                      <div className="subtle small">{row.name}</div>
                      <Link to={'/issues' + qs({ project: row.id, target_version: row.version, hide_status: 0 })}><strong>{row.version}</strong></Link>
                      {row.date_order && <span className="subtle small"> · {fmtDate(new Date(row.date_order).toISOString(), false)}</span>}
                    </td>
                    {cells(row)}
                  </tr>
                ))}
              </tbody>
              <tfoot><tr><td>{t('Total')}</td>{cells({ ...totals, needed_minutes: canTime ? totals.needed_minutes : null })}</tr></tfoot>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}

type CountRow = { name: string; open: number; resolved: number; closed: number; total: number };

const remainingCount = (row: CountRow) => Math.max(0, row.total - row.resolved - row.closed);

function CountTable({ title, rows, showProgress = false }: { title: string; rows: CountRow[]; showProgress?: boolean }) {
  const totals = {
    open: showProgress ? Math.max(0, sum(rows, 'total') - sum(rows, 'resolved') - sum(rows, 'closed')) : sum(rows, 'open'),
    resolved: sum(rows, 'resolved'),
    closed: sum(rows, 'closed'),
    total: sum(rows, 'total'),
  };
  return (
    <Panel title={title}>
      <table className="simple">
        <thead>
          <tr>
            <th></th>
            <th className="num">{t('Open')}</th>
            <th className="num">{t('Resolved')}</th>
            <th className="num">{t('Closed')}</th>
            <th className="num">{t('Total')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name}>
              <td>
                <div>{r.name === '(unassigned)' ? t('(unassigned)') : r.name}</div>
                {showProgress && <ProjectStatusBar row={r} />}
              </td>
              <td className="num">{showProgress ? remainingCount(r) : r.open}</td>
              <td className="num">{r.resolved}</td>
              <td className="num">{r.closed}</td>
              <td className="num">{r.total}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td>
              <div>{t('Total')}</div>
              {showProgress && <ProjectStatusBar row={{ name: '', ...totals }} />}
            </td>
            <td className="num">{totals.open}</td>
            <td className="num">{totals.resolved}</td>
            <td className="num">{totals.closed}</td>
            <td className="num">{totals.total}</td>
          </tr>
        </tfoot>
      </table>
    </Panel>
  );
}

function ProjectStatusBar({ row }: { row: CountRow }) {
  const open = remainingCount(row);
  const segments = [
    { key: 'closed', count: row.closed, label: t('Closed'), className: 'closed' },
    { key: 'resolved', count: row.resolved, label: t('Resolved'), className: 'resolved' },
    { key: 'open', count: open, label: t('Open'), className: 'open' },
  ].filter((segment) => segment.count > 0);
  if (row.total <= 0) return <div className="project-status-bar empty" aria-hidden="true" />;
  return (
    <div
      className="project-status-bar"
      role="img"
      aria-label={`${row.name ? `${row.name}: ` : ''}${segments.map((segment) => `${segment.label} ${segment.count}`).join(', ')}; ${t('Total')}: ${row.total}`}
      title={`${segments.map((segment) => `${segment.label}: ${segment.count}`).join(' · ')} · ${t('Total')}: ${row.total}`}
    >
      {segments.map((segment) => <span key={segment.key} className={`project-status-segment ${segment.className}`} style={{ flexGrow: segment.count }} />)}
    </div>
  );
}
